import React from 'react';
import cn from 'classnames';
import { LoadingError } from 'components/loadingError';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Container } from 'components/reactstrap';
import { useGetJobsiteFormSubmissionReportQuery } from 'apollo/generated/client-operations';
import { evalJsCode } from 'utils';
import { getDateTimeMoment } from 'utils/dates';
import { AuthContext } from 'auth';
import { PrintFormContent } from 'containers/jobsiteFormSubmission/types';
import { useDependencies } from 'containers/jobsiteFormSubmission/utils';
import { getEvalContext } from 'containers/jobsiteFormSubmission/tabs/FormSubmissionEdit.forms';
import { FormSubmissionPrintContextState, QueryStatus } from './types';
import { FormSubmissionReportLoading as ReportLoading } from './components';
import { FormSubmissionPrintView } from './formSubmission';
import { FormSubmissionPrintContext } from './FormSubmissionPrintContext';

export function FormSubmissionPrintContainer(): React.ReactElement {
  const { formSubmissionId } = useParams<{ formSubmissionId: string }>();
  const [queryStatus, setQueryStatus] = React.useState<
    Record<string, QueryStatus>
  >({});

  const { currentUser: user } = React.useContext(AuthContext);

  const { data, error, loading } = useGetJobsiteFormSubmissionReportQuery({
    variables: { id: formSubmissionId },
    fetchPolicy: 'no-cache',
  });

  const jobsiteFormSubmission = data?.getJobsiteFormSubmission;
  const { startAt, timeZone, jobsiteForm } = jobsiteFormSubmission ?? {};
  const { name: formName } = jobsiteForm?.form ?? {};

  const updateQueryStatus = (
    queryName: string,
    queryStatusItem: QueryStatus,
  ): void =>
    setQueryStatus((currentQueryStatus) => ({
      ...currentQueryStatus,
      [queryName]: queryStatusItem,
    }));

  const formSubmissionPrintContextValue =
    React.useMemo<FormSubmissionPrintContextState>(
      () => ({
        updateQueryStatus,
      }),
      [setQueryStatus],
    );

  const { dependencies: dependenciesExpressions, config: configExpression } =
    jobsiteFormSubmission?.jobsiteForm.form.content.print ?? {};
  const dependencies = useDependencies(dependenciesExpressions);

  const config = React.useMemo(() => {
    const evalContext =
      jobsiteFormSubmission &&
      dependencies &&
      getEvalContext({ dependencies, user, jobsiteFormSubmission });
    return (
      evalContext &&
      evalJsCode<PrintFormContent['config']>(configExpression, evalContext)
    );
  }, [jobsiteFormSubmission, dependencies, configExpression]);

  const areQueriesLoading =
    loading || Object.values(queryStatus).some((qs) => qs.loading);

  if (loading || error) {
    return <LoadingError loading={loading} error={error} />;
  }

  const reportMoment = getDateTimeMoment({
    date: startAt,
    timeZone,
    isUTC: true,
  });
  const reportDateText = reportMoment.format('MMM DD, YYYY');

  const PrintViewComponent = config?.component ?? FormSubmissionPrintView;

  return (
    <Container className="odin-pb-9">
      <Helmet title={`ODIN ${formName} - ${reportDateText}`} />
      <ReportLoading loading={areQueriesLoading} />
      <div
        className={cn(
          'odin-flex odin-flex-col odin-gap-y-9',
          areQueriesLoading && 'print:odin-hidden',
        )}
      >
        <FormSubmissionPrintContext.Provider
          value={formSubmissionPrintContextValue}
        >
          <PrintViewComponent
            jobsiteFormSubmission={jobsiteFormSubmission}
            config={config}
            loading={loading}
          />
        </FormSubmissionPrintContext.Provider>
      </div>
    </Container>
  );
}
